/**
 * ActionButtons Component
 *
 * Renders inline action buttons for DataTable rows.
 * Uses inline buttons instead of dropdown menus for better accessibility.
 */

import React from 'react';
import { ActionDefinition, ActionVariant } from './types';

interface ActionButtonsProps<T> {
  row: T;
  actions: ActionDefinition<T>[];
}

const variantClasses: Record<ActionVariant, string> = {
  default: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
  primary: 'text-purple-600 hover:text-purple-900 hover:bg-purple-50',
  danger: 'text-red-600 hover:text-red-900 hover:bg-red-50',
  success: 'text-green-600 hover:text-green-900 hover:bg-green-50',
  warning: 'text-amber-600 hover:text-amber-900 hover:bg-amber-50'
};

export function ActionButtons<T>({ row, actions }: ActionButtonsProps<T>) {
  const visibleActions = actions.filter(action => {
    if (action.show) {
      return action.show(row);
    }
    return true;
  });

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {visibleActions.map(action => {
        const isDisabled = action.disabled ? action.disabled(row) : false;
        const variant = action.variant || 'default';

        return (
          <button
            key={action.id}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) {
                action.onClick(row);
              }
            }}
            disabled={isDisabled}
            className={`
              p-2 rounded-lg transition-colors duration-150
              ${isDisabled
                ? 'text-gray-300 cursor-not-allowed'
                : variantClasses[variant]
              }
            `}
            title={action.tooltip}
            aria-label={action.ariaLabel || action.tooltip}
          >
            {action.icon}
          </button>
        );
      })}
    </div>
  );
}

export default ActionButtons;
